/**
 * MobileGroupManagersList
 *
 * Mobile card-based replacement for the Managers sub-view DataTable in GroupesView.
 * Shows each manager as a touch-friendly MUI Card with key info,
 * grouped by team with search and pagination.
 */
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  Divider,
  Fade,
  Pagination,
  IconButton,
} from "@mui/material";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import Langs, { Localize } from "../../../app/lang/langs";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const PAGE_SIZE = 20;

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const getStatusColor = (statut) => {
  if (!statut) return "default";
  const normalized = statut.trim().toLowerCase();
  if (["présent", "present", "actif"].includes(normalized)) return "success";
  return "error";
};

const getInitials = (prenom, nom) => {
  return (
    ((prenom || "")[0] || "").toUpperCase() +
    ((nom || "")[0] || "").toUpperCase()
  );
};

const isActiveStatus = (statut) => {
  if (!statut) return false;
  const normalized = statut.trim().toLowerCase();
  return ["présent", "present", "actif"].includes(normalized);
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function MobileGroupManagersList({
  managersList,
  getTeamLabel,
  onViewDetails,
  onRemove,
}) {
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);

  const filteredData = useMemo(() => {
    const list = managersList || [];
    if (!searchText.trim()) return list;
    const terms = searchText.toLowerCase().split(" ").filter(Boolean);
    return list.filter((mgr) => {
      const searchStr = [mgr.nom, mgr.prenom, mgr.matricule, mgr.type, mgr.email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.every((term) => searchStr.includes(term));
    });
  }, [managersList, searchText]);

  const groupedByTeam = useMemo(() => {
    const groups = {};
    filteredData.forEach((mgr) => {
      const teamKey = mgr.id_team || "__none__";
      if (!groups[teamKey]) groups[teamKey] = [];
      groups[teamKey].push(mgr);
    });
    return groups;
  }, [filteredData]);

  const teamKeys = Object.keys(groupedByTeam);
  const hasMultipleTeams = teamKeys.length > 1;

  const flatItems = useMemo(() => {
    const items = [];
    teamKeys.forEach((teamKey) => {
      if (hasMultipleTeams) {
        const label =
          teamKey === "__none__"
            ? Localize("No team")
            : getTeamLabel ? getTeamLabel(teamKey) : teamKey;
        items.push({ _type: "header", teamKey, label });
      }
      groupedByTeam[teamKey].forEach((mgr) => {
        items.push({ _type: "card", data: mgr });
      });
    });
    return items;
  }, [teamKeys, hasMultipleTeams, groupedByTeam, getTeamLabel]);

  const cardItems = flatItems.filter((item) => item._type === "card");
  const totalPages = Math.ceil(cardItems.length / PAGE_SIZE);

  const paginatedCards = cardItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const paginatedCardIds = new Set(paginatedCards.map((c) => c.data.id));

  const paginatedItems = useMemo(() => {
    const items = [];
    let lastHeader = null;
    flatItems.forEach((item) => {
      if (item._type === "header") {
        lastHeader = item;
      } else if (paginatedCardIds.has(item.data.id)) {
        if (lastHeader && !items.find((i) => i._type === "header" && i.teamKey === lastHeader.teamKey)) {
          items.push(lastHeader);
        }
        items.push(item);
      }
    });
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatItems, paginatedCardIds]);

  React.useEffect(() => {
    setPage(1);
  }, [searchText]);

  const renderCardContent = (manager) => (
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "warning.light",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: "0.9rem",
              flexShrink: 0,
            }}
          >
            {getInitials(manager.prenom, manager.nom)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: "0.9rem",
                lineHeight: 1.3,
                letterSpacing: "-0.01em",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {manager.prenom} {manager.nom}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", opacity: 0.75 }}>
              {manager.matricule}
            </Typography>
          </Box>
        </Box>
        {manager.statut && (
          <Chip
            label={manager.statut}
            size="small"
            color={getStatusColor(manager.statut)}
            variant={isActiveStatus(manager.statut) ? "filled" : "outlined"}
            sx={{ fontWeight: 600, fontSize: "0.7rem", height: 24, borderRadius: "8px", flexShrink: 0 }}
          />
        )}
      </Box>

      {manager.type && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
          <Chip
            label={manager.type}
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.7rem", height: 24, borderRadius: "8px", transition: TRANSITION_SMOOTH }}
          />
        </Box>
      )}

      {(manager.mobile_number || manager.email) && (
        <>
          <Divider sx={{ opacity: 0.3, borderColor: "rgba(0,0,0,0.06)", my: 0.75 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            {manager.mobile_number && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                <PhoneOutlinedIcon sx={{ fontSize: "1rem", color: "text.secondary", opacity: 0.6 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {manager.mobile_number}
                </Typography>
              </Box>
            )}
            {manager.email && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                <EmailOutlinedIcon sx={{ fontSize: "1rem", color: "text.secondary", opacity: 0.6 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>
                  {manager.email}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </CardContent>
  );

  const renderManagerCard = (manager, index) => (
    <Fade in key={manager.id || index} timeout={200 + index * 40}>
      <Card
        elevation={0}
        sx={{
          mb: 1.5,
          borderRadius: "16px",
          overflow: "hidden",
          bgcolor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(16px)",
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.06)",
          transition: "transform 250ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1)",
          "&:active": { transform: "scale(0.98)", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "stretch" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {onViewDetails ? (
              <CardActionArea onClick={() => onViewDetails(manager)} sx={{ p: 0 }}>
                {renderCardContent(manager)}
              </CardActionArea>
            ) : (
              renderCardContent(manager)
            )}
          </Box>
          {onRemove && (
            <Box sx={{ display: "flex", alignItems: "center", pr: 1, borderLeft: "1px solid rgba(0,0,0,0.06)", pl: 1 }}>
              <IconButton
                onClick={() => onRemove(manager)}
                aria-label={Localize("REMOVE_MANAGER")}
                sx={{ color: "#C62828", "&:hover": { bgcolor: "rgba(198, 40, 40, 0.06)" } }}
              >
                <PersonRemoveOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </Card>
    </Fade>
  );

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 2.5,
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder={Localize("Search") + "..."}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          mb: 1.5,
          "& input": { fontSize: "16px" },
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            bgcolor: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(12px)",
            transition: TRANSITION_SMOOTH,
            "& fieldset": { borderColor: "rgba(0,0,0,0.06)", transition: TRANSITION_SMOOTH },
            "&:hover fieldset": { borderColor: "rgba(0,0,0,0.15)" },
            "&.Mui-focused fieldset": { borderColor: "warning.main", boxShadow: "0 0 0 3px rgba(255,144,21,0.15)" },
          },
        }}
      />

      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, px: 0.5, fontWeight: 500, letterSpacing: "0.02em", opacity: 0.7 }}>
        {filteredData.length} {Localize("results")}
      </Typography>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.12)", borderRadius: 3 },
        }}
      >
        {paginatedItems.map((item, index) => {
          if (item._type === "header") {
            return (
              <Box
                key={`header-${item.teamKey}`}
                sx={{
                  px: 1.5, py: 1,
                  mt: index > 0 ? 1.5 : 0,
                  mb: 1,
                  borderRadius: "12px",
                  bgcolor: "rgba(255,144,21,0.06)",
                  borderLeft: "3px solid rgba(255,144,21,0.5)",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.8rem", color: "text.primary", letterSpacing: "0.01em" }}>
                  {item.label}
                </Typography>
              </Box>
            );
          }
          return renderManagerCard(item.data, index);
        })}

        {paginatedItems.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.5 }}>
                <Langs str="No results" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": { boxShadow: "0 2px 8px rgba(255,144,21,0.35)" },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
